import axiosInstance from "./api";

export const createPrompt = async ({ name, type, description = null }) => {
  if (!name) {
    throw new Error("Name is required");
  }
  if (
    ["topic", "simulant", "preprocessing", "postprocessing"].indexOf(type) ===
    -1
  ) {
    throw new Error(
      `Invalid prompt type: ${type}; must be one of 'topic', 'simulant', 'preprocessing', or 'postprocessing'`
    );
  }
  const result = await axiosInstance.post("/api/prompt/", {
    type: type,
    name: name,
    description: description,
    prompts: {
      primary: [
        {
          content: "{# primary content prompt #}",
          metadata: [
            {
              key: "name",
              value: "content",
            },
          ],
        },
      ],
    },
  });
  return result.data.data;
};

export const getPromptById = async (id) => {
  const result = await axiosInstance.get(`/api/prompt/id/${id}`);
  return result.data.data;
};

export const getPromptByKey = async (key, version = null) => {
  const versionQuery = version ? `?version=${version}` : "";
  const prompts = await axiosInstance.get(
    `/api/prompt/key/${key}${versionQuery}`
  );
  if (!prompts.data?.data?.entries.length > 0) {
    return null;
  }
  return prompts.data.data.entries[0];
};

export const getPrompts = async (type, includeDeleted = false) => {
  const result = await axiosInstance.get(
    `/api/prompt/type/${type}?include_deleted=${includeDeleted}`
  );
  return result.data?.data?.entries || [];
};

export const getPromptsByVisibility = async (
  visibility,
  keys = null,
  first = false
) => {
  const firstQuery = `?first=${first}`;
  const keysQuery = keys ? `&keys=${keys.join(",")}` : "";
  const prompt = await axiosInstance.get(
    `/api/prompt/visibility/${visibility}${firstQuery}${keysQuery}`
  );
  return prompt.data?.data?.entries || [];
};

export const updatePrompt = async (prompt) => {
  const key = prompt.key;
  delete prompt.key;
  const result = await axiosInstance.put(`/api/prompt/key/${key}`, prompt);
  return result.data.data;
};

export const deletePrompt = async (key) => {
  const prompt = await getPromptByKey(key);
  if (!prompt) {
    throw new Error(`Prompt with key ${key} not found`);
  }
  prompt.deleted = true;
  return updatePrompt(prompt);
};

export const duplicatePrompt = async (key) => {
  try {
    const original = await getPromptByKey(key);
    if (!original) {
      throw new Error(`Prompt with key ${key} not found`);
    }
    const rand = Math.floor(1000 + Math.random() * 9000);
    const newName = `${original.name} (Copy ${rand})`;
    const newPrompt = {
      ...original,
      name: newName,
      key: undefined,
      id: undefined,
      created_at: undefined,
      updated_at: undefined,
      version: undefined,
    };
    const result = await axiosInstance.post("/api/prompt/", newPrompt);
    return result.data.data;
  } catch (error) {
    console.error("Error duplicating prompt:", error);
    throw error;
  }
};

export const getReleaseNotes = async (key) => {
  const result = await axiosInstance.get(`/api/prompt/release_notes/${key}`);
  return result?.data?.data || [];
};

export const checkIfPromptDeleted = async (key) => {
  try {
    const result = await axiosInstance.get(`/api/prompt/key/${key}/is_deleted`);
    return result.data.data;
  } catch (error) {
    console.error("Error checking if prompt is deleted:", error);
    throw error;
  }
};

export const getPromptsFromTopicVersion = async (topic_version_id) => {
  try {
    const result = await axiosInstance.get(
      `/api/prompt/topic_version/${topic_version_id}`
    );
    return result.data?.data || [];
  } catch (error) {
    console.error(
      "Error fetching prompts for topic version:",
      topic_version_id,
      error
    );
    throw error;
  }
};

/**
 * Lint a prompt's content for errors and warnings
 *
 * @param {string} content - The prompt content to lint (required)
 * @param {string} [content_type='interactive'] - The type of content ('interactive' or 'python')
 * @param {Object} [variables={}] - Variables used in the prompt
 * @returns {Promise<Object>} - The linting results containing any problems found
 * @throws {Error} If content is missing or content_type is invalid
 */
export const lintPrompt = async (
  content,
  content_type = "interactive",
  variables = {}
) => {
  // Validate required parameters
  if (!content) {
    throw new Error("Content is required for linting");
  }

  // Validate content_type
  if (content_type !== "interactive" && content_type !== "python") {
    throw new Error(
      "Invalid content_type: must be either 'interactive' or 'python'"
    );
  }

  try {
    const result = await axiosInstance.post("/api/prompt/lint", {
      content,
      content_type,
      variables,
    });

    return result.data.data;
  } catch (error) {
    throw new Error(`Error setting up lint request: ${error.message}`);
  }
};

export const renderAndHighlightTemplate = async (
  template,
  variables = {},
  promptOutputs = {},
  tools = [],
  isRichFormat = false
) => {
  try {
    const result = await axiosInstance.post("/api/prompt/render/highlight", {
      template,
      variables,
      promptOutputs,
      tools,
      isRichFormat,
    });
    return result.data.data;
  } catch (error) {
    console.error("Error rendering and highlighting template:", error);
    throw error;
  }
};

export const migratePrompts = async () => {
  try {
    const result = await axiosInstance.post("/api/prompt/migrate_prompts");
    return result.data.data;
  } catch (error) {
    console.error("Error migrating prompts:", error);
    throw error;
  }
};

export const migratePromptData = async () => {
  try {
    const result = await axiosInstance.post("/api/prompt/migrate_prompt_data");
    return result.data.data;
  } catch (error) {
    console.error("Error migrating prompt data:", error);
    throw error;
  }
};

export const getRequiredVariables = async ({
  topicKey,
  version,
  promptName = null,
  fragments = null,
}) => {
  try {
    const promptNameQuery = promptName ? `&prompt_name=${promptName}` : "";
    const fragmentQuery = fragments ? `&fragments=${fragments.join(",")}` : "";
    const response = await axiosInstance.get(
      `/api/prompt/required_vars?topic_key=${topicKey}&version=${parseInt(
        version
      )}${promptNameQuery}${fragmentQuery}`
    );
    return response.data.data.required_variables;
  } catch (error) {
    throw error;
  }
};

// Export the function with the rest of the service exports
export const promptService = {
  createPrompt,
  getPromptById,
  getPromptByKey,
  getPrompts,
  getPromptsByVisibility,
  updatePrompt,
  deletePrompt,
  duplicatePrompt,
  getReleaseNotes,
  lintPrompt,
};

export default promptService;
